import generatedContent from './generated-content.json'
import type { SupportedLocale } from '../types/simulation'
import type { LegalDocument } from '../data/legal'
import type { Inspiration } from '../video/data/inspirations'
import type { PlatformPreset } from '../video/domain/types'

type LocalizedLegalDocument = Omit<LegalDocument, 'version' | 'sections' | 'references'> & {
  sections: Array<Pick<LegalDocument['sections'][number], 'title' | 'paragraphs' | 'bullets' | 'note'>>
  references?: Array<Pick<LegalDocument['references'][number], 'label'>>
}

interface LocalizedContentPack {
  legal: Record<'terms' | 'privacy', LocalizedLegalDocument>
  inspirations: Record<string, Pick<Inspiration, 'category' | 'title' | 'description' | 'briefSeed' | 'tone'>>
  presets: Record<string, string>
}

function contentPack(locale: SupportedLocale): LocalizedContentPack | undefined {
  if (locale === 'tr') return undefined
  return (generatedContent as unknown as Partial<Record<SupportedLocale, LocalizedContentPack>>)[locale]
}

export function localizeLegalDocument(
  kind: 'terms' | 'privacy',
  locale: SupportedLocale,
  document: LegalDocument,
): LegalDocument {
  if (locale === 'tr') return document
  const translated = contentPack(locale)?.legal[kind]
  if (!translated || translated.sections.length !== document.sections.length) {
    throw new Error(`Missing current ${kind} translation for ${locale}`)
  }
  const formatDate = (value: string) => new Intl.DateTimeFormat(locale, {
    dateStyle: 'long', timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`))
  return {
    ...document,
    eyebrow: translated.eyebrow,
    title: translated.title,
    lead: translated.lead,
    effectiveDate: formatDate(translated.effectiveDate),
    lastUpdated: formatDate(translated.lastUpdated),
    sections: document.sections.map((section, index) => ({ ...translated.sections[index]!, id: section.id })),
    references: document.references.map((reference, index) => ({ ...reference, label: translated.references?.[index]?.label ?? reference.label })),
  }
}

export function localizeInspiration(
  inspiration: Inspiration | undefined,
  locale: SupportedLocale,
): Inspiration | undefined {
  if (!inspiration) return undefined
  const translated = contentPack(locale)?.inspirations[inspiration.id]
  return translated ? { ...inspiration, ...translated } : inspiration
}

export function localizePlatformPreset<T extends PlatformPreset>(preset: T, locale: SupportedLocale): T {
  const label = contentPack(locale)?.presets[preset.id]
  return label ? { ...preset, label } : preset
}

export function localizePlatformPresets<T extends PlatformPreset>(presets: readonly T[], locale: SupportedLocale): readonly T[] {
  return presets.map((preset) => localizePlatformPreset(preset, locale))
}
